// ============================================================================
// ImageCropModal — full crop editor: overlay, aspect presets, save/delete
// ============================================================================

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Badge,
  Box,
  Button,
  Flex,
  HStack,
  Image,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  NumberDecrementStepper,
  NumberIncrementStepper,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  Text,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import { ADMIN_COLORS } from '../../../../../../generated/tokens/constants';
import { useCropPoints, useSaveCropPoints, useDeleteCropPoints } from '../hooks/useCropPoints';
import { CropOverlay } from './CropOverlay';
import { ZoomToolbar } from './ZoomToolbar';
import { useZoomPan } from '../hooks/useZoomPan';
import {
  ASPECT_PRESETS,
  clamp,
  clientToPercent,
  computeOutputDimensions,
  enforceAspectRatio,
  getContainedRectFromSize,
  MIN_CROP_PERCENT,
  type AspectPreset,
  type CropRect,
} from '../utils/cropUtils';

interface ImageCropModalProps {
  imageId: number;
  imageUrl: string;
  naturalWidth: number;
  naturalHeight: number;
  isOpen: boolean;
  onClose: () => void;
  onSaved?: () => void;
}

const DEFAULT_CROP: CropRect = { topLeftX: 0, topLeftY: 0, bottomRightX: 100, bottomRightY: 100 };

export function ImageCropModal({
  imageId,
  imageUrl,
  naturalWidth: propNatW,
  naturalHeight: propNatH,
  isOpen,
  onClose,
  onSaved,
}: ImageCropModalProps) {
  const toast = useToast();
  const imageRef = useRef<HTMLImageElement>(null);
  const cancelRef = useRef<HTMLButtonElement>(null);
  const { isOpen: isDeleteOpen, onOpen: onDeleteOpen, onClose: onDeleteClose } = useDisclosure();

  // Zoom + pan
  const [zoomState, zoomActions] = useZoomPan(isOpen);
  const zoomTransform = useMemo(
    () =>
      zoomState.zoom === 1 && zoomState.panX === 0 && zoomState.panY === 0
        ? undefined
        : `scale(${zoomState.zoom}) translate(${zoomState.panX}px, ${zoomState.panY}px)`,
    [zoomState],
  );

  // Self-detect natural dimensions from image onLoad
  const [detectedW, setDetectedW] = useState(0);
  const [detectedH, setDetectedH] = useState(0);
  const naturalWidth = propNatW > 0 ? propNatW : detectedW;
  const naturalHeight = propNatH > 0 ? propNatH : detectedH;

  const handleImageLoad = useCallback(() => {
    const img = imageRef.current;
    if (img) {
      setDetectedW(img.naturalWidth);
      setDetectedH(img.naturalHeight);
    }
  }, []);

  // Fetch existing crop points
  const { data: existingCrop, isLoading: isLoadingCrop } = useCropPoints(isOpen ? imageId : null);
  const saveMutation = useSaveCropPoints();
  const deleteMutation = useDeleteCropPoints();

  // Local crop state
  const [crop, setCrop] = useState<CropRect>(DEFAULT_CROP);
  const [selectedPreset, setSelectedPreset] = useState<string>('Free');
  const [hasInitialized, setHasInitialized] = useState(false);
  const [hasCrop, setHasCrop] = useState(false);
  const [isDrawing, setIsDrawing] = useState(false);
  const drawOriginRef = useRef<{ x: number; y: number } | null>(null);
  const [drawBounds, setDrawBounds] = useState({ offsetX: 0, offsetY: 0, visibleW: 0, visibleH: 0 });

  // Clear the current crop selection (back to draw-to-create state)
  const clearCrop = useCallback(() => {
    setCrop(DEFAULT_CROP);
    setHasCrop(false);
    setIsDrawing(false);
    drawOriginRef.current = null;
  }, []);

  // Keyboard shortcuts: Delete/Backspace clears crop, Escape clears or closes
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: KeyboardEvent) => {
      // Ignore if user is typing in an input
      const tag = (e.target as HTMLElement).tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;

      if (e.key === 'Delete' || e.key === 'Backspace') {
        if (hasCrop) {
          e.preventDefault();
          clearCrop();
        }
      } else if (e.key === 'Escape') {
        if (hasCrop) {
          e.preventDefault();
          e.stopPropagation(); // Prevent Chakra Modal from closing
          clearCrop();
        }
        // If no crop, Escape will close the modal (Chakra default)
      } else if (e.key === '+' || e.key === '=') {
        e.preventDefault();
        zoomActions.zoomIn();
      } else if (e.key === '-' || e.key === '_') {
        e.preventDefault();
        zoomActions.zoomOut();
      } else if (e.key === '0') {
        e.preventDefault();
        zoomActions.resetZoom();
      }
    };
    // Use capture phase so we intercept before Chakra Modal's closeOnEsc
    window.addEventListener('keydown', handler, true);
    return () => window.removeEventListener('keydown', handler, true);
  }, [isOpen, hasCrop, clearCrop, zoomActions]);

  // Initialize crop from fetched data
  useEffect(() => {
    if (!isOpen) {
      setHasInitialized(false);
      setHasCrop(false);
      setSelectedPreset('Free');
      zoomActions.resetZoom();
      return;
    }
    if (isLoadingCrop || hasInitialized) return;

    if (existingCrop) {
      setCrop({
        topLeftX: existingCrop.top_left_x,
        topLeftY: existingCrop.top_left_y,
        bottomRightX: existingCrop.bottom_right_x,
        bottomRightY: existingCrop.bottom_right_y,
      });
      setHasCrop(true);
    } else {
      setCrop(DEFAULT_CROP);
      setHasCrop(false);
    }
    setHasInitialized(true);
  }, [isOpen, isLoadingCrop, existingCrop, hasInitialized]);

  // Aspect ratio selection
  const handlePresetSelect = useCallback(
    (preset: AspectPreset) => {
      setSelectedPreset(preset.label);
      if (preset.ratio != null && naturalWidth > 0 && naturalHeight > 0) {
        setCrop((prev) => enforceAspectRatio(prev, preset.ratio, naturalWidth, naturalHeight));
      }
    },
    [naturalWidth, naturalHeight],
  );

  const activeRatio = ASPECT_PRESETS.find((p) => p.label === selectedPreset)?.ratio ?? null;

  const handleCropChange = useCallback(
    (newCrop: CropRect) => {
      setCrop(newCrop);
      if (!hasCrop) setHasCrop(true);
    },
    [hasCrop],
  );

  // Numeric input changes
  const handleNumericChange = useCallback(
    (field: keyof CropRect, value: string) => {
      const num = parseFloat(value);
      if (isNaN(num)) return;
      setCrop((prev) => {
        const updated = { ...prev, [field]: num };
        if (activeRatio != null && naturalWidth > 0 && naturalHeight > 0) {
          return enforceAspectRatio(updated, activeRatio, naturalWidth, naturalHeight);
        }
        return updated;
      });
      if (!hasCrop) setHasCrop(true);
    },
    [activeRatio, naturalWidth, naturalHeight, hasCrop],
  );

  // Save
  const handleSave = useCallback(async () => {
    try {
      await saveMutation.mutateAsync({
        imageId,
        topLeftX: Math.round(crop.topLeftX * 100) / 100,
        topLeftY: Math.round(crop.topLeftY * 100) / 100,
        bottomRightX: Math.round(crop.bottomRightX * 100) / 100,
        bottomRightY: Math.round(crop.bottomRightY * 100) / 100,
      });
      toast({ title: 'Crop saved', status: 'success', duration: 2000 });
      onSaved?.();
      onClose();
    } catch {
      toast({ title: 'Failed to save crop', status: 'error', duration: 3000 });
    }
  }, [crop, imageId, saveMutation, toast, onSaved, onClose]);

  // Delete
  const handleDeleteConfirm = useCallback(async () => {
    onDeleteClose();
    try {
      await deleteMutation.mutateAsync(imageId);
      toast({ title: 'Crop removed', status: 'success', duration: 2000 });
      onSaved?.();
      onClose();
    } catch {
      toast({ title: 'Failed to remove crop', status: 'error', duration: 3000 });
    }
  }, [imageId, deleteMutation, toast, onSaved, onClose, onDeleteClose]);

  const outputDims =
    naturalWidth > 0 && naturalHeight > 0
      ? computeOutputDimensions(crop, naturalWidth, naturalHeight)
      : null;

  // TODO: Auto-generated crop detection needs backend signal (computed crops from
  // computeComicCoverAssetCropPointsFromImage are not stored in images_crop_points)
  const isAutoGenerated = false;
  const standardPresets = ASPECT_PRESETS.filter((p) => p.group === 'standard');
  const gcPresets = ASPECT_PRESETS.filter((p) => p.group === 'gc');

  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose} size="4xl" isCentered>
        <ModalOverlay bg={ADMIN_COLORS.overlay} />
        <ModalContent bg={ADMIN_COLORS.card} gc-testing-id="image-crop-modal">
          <ModalCloseButton gc-testing-id="crop-modal-close" color={ADMIN_COLORS.textMuted} />
          <ModalHeader color={ADMIN_COLORS.textPrimary} display="flex" alignItems="center" gap={2}>
            Crop Image
            {isAutoGenerated && (
              <Badge colorScheme="yellow" fontSize="2xs">
                Auto-generated
              </Badge>
            )}
          </ModalHeader>

          <ModalBody pb={4}>
            {/* Image container */}
            {/* Outer clip container — scroll to zoom, toolbar floats here */}
            <Box
              ref={zoomActions.setContainerRef}
              gc-testing-id="crop-image-container"
              position="relative"
              maxH="60vh"
              bg={ADMIN_COLORS.elevated}
              borderRadius="md"
              overflow="hidden"
            >
              {/* Zoom toolbar — floats outside transform */}
              <Box position="absolute" top={2} right={2} zIndex={20}>
                <ZoomToolbar
                  zoom={zoomState.zoom}
                  onZoomIn={zoomActions.zoomIn}
                  onZoomOut={zoomActions.zoomOut}
                  onReset={zoomActions.resetZoom}
                />
              </Box>

              {/* Clear crop button — also outside transform */}
              {hasCrop && (
                <Button
                  gc-testing-id="crop-clear-btn"
                  size="xs"
                  position="absolute"
                  top={2}
                  left={2}
                  zIndex={20}
                  bg="rgba(0,0,0,0.7)"
                  color="white"
                  _hover={{ bg: 'rgba(0,0,0,0.9)' }}
                  leftIcon={<Text fontSize="xs" lineHeight={1}>✕</Text>}
                  onClick={clearCrop}
                >
                  Clear
                </Button>
              )}

              {/* Zoom + pan wrapper — image & overlays scale together */}
              {/* Zoom + pan wrapper — image & overlays scale together */}
              {/* Pan: hold Space + drag (when zoomed >1x) */}
              <Box
                position="relative"
                display="flex"
                justifyContent="center"
                maxH="60vh"
                transform={zoomTransform}
                transformOrigin="center center"
                transition={zoomActions.isPanning ? undefined : 'transform 0.1s ease-out'}
                cursor={
                  zoomActions.isPanning
                    ? 'grabbing'
                    : zoomActions.isSpaceHeld && zoomState.zoom > 1
                      ? 'grab'
                      : undefined
                }
                onPointerDown={zoomActions.handlePanPointerDown}
                onPointerMove={zoomActions.isPanning ? zoomActions.handlePanPointerMove : undefined}
                onPointerUp={zoomActions.isPanning ? zoomActions.handlePanPointerUp : undefined}
              >
                <Image
                  ref={imageRef}
                  src={imageUrl}
                  alt="Crop preview"
                  objectFit="contain"
                  maxH="60vh"
                  onLoad={handleImageLoad}
                  userSelect="none"
                  draggable={false}
                />
                {hasCrop && naturalWidth > 0 && naturalHeight > 0 && (
                  <CropOverlay
                    imageRef={imageRef}
                    crop={crop}
                    onChange={handleCropChange}
                    aspectRatio={activeRatio}
                    naturalWidth={naturalWidth}
                    naturalHeight={naturalHeight}
                  />
                )}
                {(!hasCrop || isDrawing) && (
                  <Box
                    position="absolute"
                    inset={0}
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    bg={isDrawing ? 'transparent' : 'rgba(0,0,0,0.3)'}
                    cursor="crosshair"
                    gc-testing-id="crop-hint-overlay"
                    onPointerDown={(e) => {
                      if (naturalWidth <= 0 || naturalHeight <= 0) return;
                      const img = imageRef.current;
                      if (!img) return;
                      const rect = img.getBoundingClientRect();
                      e.preventDefault();
                      (e.target as HTMLElement).setPointerCapture(e.pointerId);
                      // Use layout-space dimensions (offsetWidth/Height) for draw preview
                      // positioning inside the zoom wrapper (not screen-space getBoundingClientRect)
                      const contained = getContainedRectFromSize(
                        img.offsetWidth,
                        img.offsetHeight,
                        naturalWidth,
                        naturalHeight,
                      );
                      setDrawBounds({
                        offsetX: img.offsetLeft + contained.offsetX,
                        offsetY: img.offsetTop + contained.offsetY,
                        visibleW: contained.visibleW,
                        visibleH: contained.visibleH,
                      });
                      // clientToPercent still uses screen-space rect (correct for mouse → percent)
                      const pct = clientToPercent(e.clientX, e.clientY, rect, naturalWidth, naturalHeight);
                      drawOriginRef.current = pct;
                      setIsDrawing(true);
                      setCrop({
                        topLeftX: pct.x,
                        topLeftY: pct.y,
                        bottomRightX: pct.x,
                        bottomRightY: pct.y,
                      });
                    }}
                    onPointerMove={(e) => {
                      if (!isDrawing || !drawOriginRef.current) return;
                      const rect = imageRef.current?.getBoundingClientRect();
                      if (!rect) return;
                      const pct = clientToPercent(e.clientX, e.clientY, rect, naturalWidth, naturalHeight);
                      const origin = drawOriginRef.current;
                      let newCrop: CropRect = {
                        topLeftX: Math.min(origin.x, pct.x),
                        topLeftY: Math.min(origin.y, pct.y),
                        bottomRightX: Math.max(origin.x, pct.x),
                        bottomRightY: Math.max(origin.y, pct.y),
                      };
                      if (activeRatio != null) {
                        newCrop = enforceAspectRatio(newCrop, activeRatio, naturalWidth, naturalHeight);
                      }
                      setCrop(newCrop);
                    }}
                    onPointerUp={(e) => {
                      if (!isDrawing) return;
                      (e.target as HTMLElement).releasePointerCapture(e.pointerId);
                      setIsDrawing(false);
                      drawOriginRef.current = null;
                      // If the user just clicked without dragging, give a reasonable default
                      const w = crop.bottomRightX - crop.topLeftX;
                      const h = crop.bottomRightY - crop.topLeftY;
                      if (w < MIN_CROP_PERCENT && h < MIN_CROP_PERCENT) {
                        const cx = crop.topLeftX;
                        const cy = crop.topLeftY;
                        let fallback: CropRect = {
                          topLeftX: clamp(cx - 15, 0, 70),
                          topLeftY: clamp(cy - 15, 0, 70),
                          bottomRightX: clamp(cx + 15, 30, 100),
                          bottomRightY: clamp(cy + 15, 30, 100),
                        };
                        if (activeRatio != null) {
                          fallback = enforceAspectRatio(fallback, activeRatio, naturalWidth, naturalHeight);
                        }
                        setCrop(fallback);
                      }
                      // NOW commit — overlay unmounts, CropOverlay takes over
                      setHasCrop(true);
                    }}
                  >
                    {/* Live draw preview — positioned over the actual image, not the container */}
                    {isDrawing && (crop.bottomRightX - crop.topLeftX > 0.5 || crop.bottomRightY - crop.topLeftY > 0.5) && (
                      <Box
                        position="absolute"
                        left={`${drawBounds.offsetX}px`}
                        top={`${drawBounds.offsetY}px`}
                        width={`${drawBounds.visibleW}px`}
                        height={`${drawBounds.visibleH}px`}
                        pointerEvents="none"
                      >
                        {/* Dimming regions */}
                        <Box position="absolute" top={0} left={0} right={0} height={`${crop.topLeftY}%`} bg="rgba(0,0,0,0.5)" />
                        <Box position="absolute" bottom={0} left={0} right={0} top={`${crop.bottomRightY}%`} bg="rgba(0,0,0,0.5)" />
                        <Box position="absolute" top={`${crop.topLeftY}%`} left={0} width={`${crop.topLeftX}%`} height={`${crop.bottomRightY - crop.topLeftY}%`} bg="rgba(0,0,0,0.5)" />
                        <Box position="absolute" top={`${crop.topLeftY}%`} right={0} left={`${crop.bottomRightX}%`} height={`${crop.bottomRightY - crop.topLeftY}%`} bg="rgba(0,0,0,0.5)" />
                        {/* Selection outline */}
                        <Box
                          position="absolute"
                          left={`${crop.topLeftX}%`}
                          top={`${crop.topLeftY}%`}
                          width={`${crop.bottomRightX - crop.topLeftX}%`}
                          height={`${crop.bottomRightY - crop.topLeftY}%`}
                          border="1px dashed white"
                        />
                      </Box>
                    )}
                    {!isDrawing && (
                      <Text
                        color="white"
                        fontSize="sm"
                        fontWeight="medium"
                        textShadow="0 1px 3px rgba(0,0,0,0.6)"
                        pointerEvents="none"
                      >
                        Click and drag to define crop region
                      </Text>
                    )}
                  </Box>
                )}
              </Box>
            </Box>

            {/* Aspect ratio presets */}
            <Box mt={4}>
              <Flex align="center" gap={2} mb={2} flexWrap="wrap">
                <Text fontSize="xs" color={ADMIN_COLORS.textMuted} minW="65px">
                  Standard:
                </Text>
                <HStack spacing={1} flexWrap="wrap">
                  {standardPresets.map((preset) => (
                    <Button
                      key={preset.label}
                      gc-testing-id={`crop-preset-${preset.label.toLowerCase().replace(/[^a-z0-9]/g, '-')}`}
                      size="xs"
                      variant={selectedPreset === preset.label ? 'solid' : 'outline'}
                      bg={selectedPreset === preset.label ? ADMIN_COLORS.brand : 'transparent'}
                      color={selectedPreset === preset.label ? 'white' : ADMIN_COLORS.textSecondary}
                      borderColor={ADMIN_COLORS.border}
                      _hover={{ bg: selectedPreset === preset.label ? ADMIN_COLORS.brand : ADMIN_COLORS.hoverBg }}
                      onClick={() => handlePresetSelect(preset)}
                    >
                      {preset.label}
                    </Button>
                  ))}
                </HStack>
              </Flex>
              <Flex align="center" gap={2} flexWrap="wrap">
                <Text fontSize="xs" color={ADMIN_COLORS.textMuted} minW="65px">
                  GC:
                </Text>
                <HStack spacing={1} flexWrap="wrap">
                  {gcPresets.map((preset) => (
                    <Button
                      key={preset.label}
                      gc-testing-id={`crop-preset-${preset.label.toLowerCase().replace(/[^a-z0-9]/g, '-')}`}
                      size="xs"
                      variant={selectedPreset === preset.label ? 'solid' : 'outline'}
                      bg={selectedPreset === preset.label ? ADMIN_COLORS.brand : 'transparent'}
                      color={selectedPreset === preset.label ? 'white' : ADMIN_COLORS.textSecondary}
                      borderColor={ADMIN_COLORS.border}
                      _hover={{ bg: selectedPreset === preset.label ? ADMIN_COLORS.brand : ADMIN_COLORS.hoverBg }}
                      onClick={() => handlePresetSelect(preset)}
                    >
                      {preset.label}
                    </Button>
                  ))}
                </HStack>
              </Flex>
            </Box>

            {/* Numeric inputs + output dimensions */}
            <Flex mt={4} gap={3} align="center" flexWrap="wrap">
              {(
                [
                  ['topLeftX', 'TL X'],
                  ['topLeftY', 'TL Y'],
                  ['bottomRightX', 'BR X'],
                  ['bottomRightY', 'BR Y'],
                ] as const
              ).map(([field, label]) => (
                <Box key={field}>
                  <Text fontSize="2xs" color={ADMIN_COLORS.textMuted} mb={0.5}>
                    {label}
                  </Text>
                  <NumberInput
                    gc-testing-id={`crop-input-${field}`}
                    size="xs"
                    w="70px"
                    step={0.1}
                    precision={2}
                    min={0}
                    max={100}
                    value={crop[field].toFixed(2)}
                    onChange={(val) => handleNumericChange(field, val)}
                  >
                    <NumberInputField {...({ bg: ADMIN_COLORS.card, borderColor: ADMIN_COLORS.border } as Record<string, string>)} />
                    <NumberInputStepper>
                      <NumberIncrementStepper borderColor={ADMIN_COLORS.border} />
                      <NumberDecrementStepper borderColor={ADMIN_COLORS.border} />
                    </NumberInputStepper>
                  </NumberInput>
                </Box>
              ))}
              {outputDims && (
                <Text fontSize="xs" color={ADMIN_COLORS.textSecondary} ml="auto">
                  Output: {outputDims.width} × {outputDims.height} px
                  {naturalWidth > 0 && ` (from ${naturalWidth} × ${naturalHeight})`}
                </Text>
              )}
            </Flex>
          </ModalBody>

          <ModalFooter justifyContent="space-between">
            <Button
              gc-testing-id="crop-remove-btn"
              variant="outline"
              colorScheme="red"
              size="sm"
              onClick={onDeleteOpen}
              isDisabled={!existingCrop}
              isLoading={deleteMutation.isPending}
            >
              Remove Crop
            </Button>
            <HStack spacing={2}>
              <Button
                gc-testing-id="crop-cancel-btn"
                variant="ghost"
                size="sm"
                color={ADMIN_COLORS.textPrimary}
                onClick={onClose}
              >
                Cancel
              </Button>
              <Button
                gc-testing-id="crop-save-btn"
                size="sm"
                bg={ADMIN_COLORS.brand}
                color="white"
                _hover={{ opacity: 0.9 }}
                onClick={handleSave}
                isLoading={saveMutation.isPending}
              >
                Save Crop
              </Button>
            </HStack>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Delete confirmation */}
      <AlertDialog isOpen={isDeleteOpen} leastDestructiveRef={cancelRef} onClose={onDeleteClose}>
        <AlertDialogOverlay bg={ADMIN_COLORS.overlay}>
          <AlertDialogContent bg={ADMIN_COLORS.card} borderColor={ADMIN_COLORS.border}>
            <AlertDialogHeader fontSize="lg" fontWeight="bold" color={ADMIN_COLORS.textPrimary}>
              Remove Crop
            </AlertDialogHeader>
            <AlertDialogBody color={ADMIN_COLORS.textSecondary}>
              Are you sure you want to remove the crop points for this image? The original image will be shown without cropping.
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button
                gc-testing-id="crop-delete-cancel"
                ref={cancelRef}
                onClick={onDeleteClose}
                variant="ghost"
                color={ADMIN_COLORS.textPrimary}
              >
                Cancel
              </Button>
              <Button
                gc-testing-id="crop-delete-confirm"
                colorScheme="red"
                onClick={handleDeleteConfirm}
                ml={3}
                isLoading={deleteMutation.isPending}
              >
                Remove
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </>
  );
}
